import React, { useState } from 'react';
import { KnowledgeBaseFolder } from '../../api/knowledge-base';
import { FolderIcon, FolderOpenIcon, ChevronRightIcon, ChevronDownIcon } from 'lucide-react';

interface FolderTreeProps {
  folders: KnowledgeBaseFolder[];
  selectedFolder: KnowledgeBaseFolder | null;
  onFolderSelect: (folder: KnowledgeBaseFolder | null) => void;
}

export const FolderTree: React.FC<FolderTreeProps> = ({
  folders,
  selectedFolder,
  onFolderSelect,
}) => {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

  const toggleFolder = (folderId: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const renderFolder = (folder: KnowledgeBaseFolder, level = 0) => {
    const isExpanded = expandedFolders.has(folder.id);
    const isSelected = selectedFolder?.id === folder.id;
    const hasChildren = folder.children && folder.children.length > 0;

    return (
      <div key={folder.id}>
        <div
          className={`flex items-center px-2 py-2 hover:bg-gray-100 cursor-pointer rounded-md ${
            isSelected ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
          }`}
          style={{ paddingLeft: `${level * 16 + 8}px` }}
          onClick={() => onFolderSelect(folder)}
        >
          {hasChildren && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleFolder(folder.id);
              }}
              className="mr-1"
            >
              {isExpanded ? (
                <ChevronDownIcon className="w-4 h-4" />
              ) : (
                <ChevronRightIcon className="w-4 h-4" />
              )}
            </button>
          )}
          {!hasChildren && <div className="w-5" />}
          {isExpanded ? (
            <FolderOpenIcon className="w-4 h-4 mr-2 flex-shrink-0" />
          ) : (
            <FolderIcon className="w-4 h-4 mr-2 flex-shrink-0" />
          )}
          <span className="text-sm truncate flex-1">{folder.name}</span>
          {folder.documentCount !== undefined && folder.documentCount > 0 && (
            <span className="text-xs text-gray-500 ml-2">{folder.documentCount}</span>
          )}
        </div>
        {hasChildren && isExpanded && (
          <div>
            {folder.children!.map((child) => renderFolder(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-1">
      <div
        className={`flex items-center px-2 py-2 hover:bg-gray-100 cursor-pointer rounded-md ${
          !selectedFolder ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
        }`}
        onClick={() => onFolderSelect(null)}
      >
        <FolderIcon className="w-4 h-4 mr-2" />
        <span className="text-sm font-medium">All Documents</span>
      </div>
      {folders.map((folder) => renderFolder(folder))}
    </div>
  );
};
